import { IconType } from "react-icons";
import {
  FiCheckCircle,
  FiFilter,
  FiFileText,
  FiPhone,
  FiMessageCircle,
  FiSquare,
  FiTarget,
  FiUserCheck,
  FiSend
} from "react-icons/fi";
import { StateProps } from "./classes/state-machine/state";
import { AstronautIcon } from "components/vectors/astronaut-icon";
import { IconProps } from "@chakra-ui/react";

export type PipelineBlockCategory =
  | "ORIGEM"
  | "PROCESSAMENTO"
  | "CANAIS"
  | "CONTROLE"
  | "ENCERRAMENTO";

export interface PipelineBlockDefinition {
  operation: StateProps["operation"];
  label: string;
  subtitle: string;
  category: PipelineBlockCategory;
  accentColor: string;
  softColor: string;
  icon: IconType | ((props: IconProps) => JSX.Element);
  fixed?: boolean;
}

export const PIPELINE_DRAG_TYPE = "application/pipeline-block";

export const PIPELINE_BLOCKS: PipelineBlockDefinition[] = [
  {
    operation: "START",
    label: "Origem CSV",
    subtitle: "Arquivo CSV",
    category: "ORIGEM",
    accentColor: "#2F80ED",
    softColor: "#EAF2FD",
    icon: FiFileText,
    fixed: true
  },
  {
    operation: "HIGIENIZACAO_OFFLINE",
    label: "Higienização Offline",
    subtitle: "Valida e filtra a base",
    category: "PROCESSAMENTO",
    accentColor: "#4DA8A1",
    softColor: "#EAF7F6",
    icon: FiCheckCircle
  },
  {
    operation: "ANALISE_IA",
    label: "AnaliseAI",
    subtitle: "Classificação de oportunidade INSS",
    category: "PROCESSAMENTO",
    accentColor: "#06B6D4",
    softColor: "#E6FAFC",
    icon: AstronautIcon
  },
  {
    operation: "REFIN",
    label: "Refin. Bancos",
    subtitle: "Higienização de refin",
    category: "PROCESSAMENTO",
    accentColor: "#6C7EE1",
    softColor: "#EEF1FD",
    icon: FiTarget
  },
  // {
  //   operation: "SAQUE_COMPLEMENTAR",
  //   label: "Saque complementar",
  //   subtitle: "Higienização de saque",
  //   category: "PROCESSAMENTO",
  //   accentColor: "#D8896B",
  //   softColor: "#FDF0EB",
  //   icon: FiTarget
  // },
  {
    operation: "FILTRO",
    label: "Filtros",
    subtitle: "Critérios de segmentação",
    category: "PROCESSAMENTO",
    accentColor: "#9B7BD8",
    softColor: "#F3EEFB",
    icon: FiFilter
  },
  {
    operation: "TELEFONIA",
    label: "Telefonia",
    subtitle: "Api telefonia",
    category: "PROCESSAMENTO",
    accentColor: "#E89A2F",
    softColor: "#FFF6EA",
    icon: FiPhone
  },
  {
    operation: "SMS",
    label: "SMS",
    subtitle: "Envio de mensagem",
    category: "CANAIS",
    accentColor: "#6B5BFF",
    softColor: "#F1EEFF",
    icon: FiMessageCircle
  },
  {
    operation: "WHATS",
    label: "WhatsApp",
    subtitle: "Mensagens via WhatsApp",
    category: "CANAIS",
    accentColor: "#3ABB78",
    softColor: "#EAF9F0",
    icon: FiMessageCircle
  },
  {
    operation: "DIGITACAO",
    label: "Digitação",
    subtitle: "Fila de digitação",
    category: "CONTROLE",
    accentColor: "#C9994A",
    softColor: "#FCF4E8",
    icon: FiUserCheck
  },
  {
    operation: "ENVIA_ATENDIMENTO",
    label: "Envio Atendimento",
    subtitle: "Envia para atendimento",
    category: "CANAIS",
    accentColor: "#E06B9A",
    softColor: "#FDEEF4",
    icon: FiSend
  },
  {
    operation: "END",
    label: "Fim",
    subtitle: "Encerra o fluxo",
    category: "ENCERRAMENTO",
    accentColor: "#E24A4A",
    softColor: "#FDEEEE",
    icon: FiSquare,
    fixed: true
  }
];

export const CATEGORY_TITLE: Record<PipelineBlockCategory, string> = {
  ORIGEM: "ORIGEM",
  PROCESSAMENTO: "PROCESSAMENTO",
  CANAIS: "CANAIS",
  CONTROLE: "CONTROLE",
  ENCERRAMENTO: "ENCERRAMENTO"
};

export const BLOCKS_BY_OPERATION = PIPELINE_BLOCKS.reduce(
  (acc, item) => {
    acc[item.operation] = item;
    return acc;
  },
  {} as Record<StateProps["operation"], PipelineBlockDefinition>
);

export const STATE_OPERATION_LABELS = PIPELINE_BLOCKS.reduce(
  (acc, item) => {
    acc[item.operation] = item.label;
    return acc;
  },
  {} as Record<StateProps["operation"], string>
);

export function getBlockDefinition(operation: StateProps["operation"]) {
  return BLOCKS_BY_OPERATION[operation];
}

export function isFixedBoundaryOperation(operation: StateProps["operation"]) {
  return operation === "START" || operation === "END";
}

export function getTransitionEdgeColor(operation: StateProps["operation"]) {
  return getBlockDefinition(operation)?.accentColor ?? "#94A3B8";
}

export function getDefaultNodeLabel(
  operation: StateProps["operation"],
  mailingOrigin?: string,
  qtdGerada?: number | null
): { title: string; subtitle: string } {
  const definition = BLOCKS_BY_OPERATION[operation];
  const defaultSubtitle = definition?.subtitle ?? "";

  if (operation === "START") {
    if (mailingOrigin === "UPLOAD_MANUAL") {
      return {
        title: "Upload mailing",
        subtitle: qtdGerada
          ? `Arquivo CSV (${qtdGerada} leads)`
          : "Upload manual"
      };
    }
    if (mailingOrigin === "API_MULTICORBAN") {
      return {
        title: "Geração de mailing",
        subtitle: qtdGerada ? `${qtdGerada} leads gerados` : "Via API"
      };
    }
  }

  return {
    title: STATE_OPERATION_LABELS[operation] ?? operation,
    subtitle: defaultSubtitle
  };
}

export function getEventLabel(event: "SUCCESS" | "ERROR" | "START") {
  if (event === "SUCCESS") return "Sucesso";
  if (event === "ERROR") return "Erro";
  return "Start";
}

export const CATEGORY_ORDER: PipelineBlockCategory[] = [
  "ORIGEM",
  "PROCESSAMENTO",
  "CONTROLE",
  "CANAIS",
  "ENCERRAMENTO"
];

export const SUPPORTS_MULTIPLE_EXECUTORS: StateProps["operation"][] = [
  "REFIN",
  "SAQUE_COMPLEMENTAR"
];

export const CHANNEL_OPERATIONS: StateProps["operation"][] = [
  "SMS",
  "WHATS",
  "ENVIA_ATENDIMENTO"
];

export const CONTROL_OPERATIONS: StateProps["operation"][] = ["DIGITACAO"];

export const PROCESSING_OPERATIONS: StateProps["operation"][] = [
  "HIGIENIZACAO_OFFLINE",
  "ANALISE_IA",
  "REFIN",
  "SAQUE_COMPLEMENTAR",
  "FILTRO",
  "TELEFONIA"
];

export const NON_DROPPABLE_OPERATIONS: StateProps["operation"][] = [
  "START",
  "END"
];

export const NODE_DEFAULT_HEIGHT = 72;

export const CREATE_PIPELINE_BUTTON_LABEL = "Criar Pipeline";

export const DOWNLOAD_CSV_BUTTON_LABEL = "Baixar CSV";

export const SEND_CHANNEL_LABEL = "Enviar para";

export const DEFAULT_EDGE_TYPE = "smoothstep";

export const DEFAULT_EDGE_DASH = "6 6";

export const DEFAULT_EVENT: TransitionProps["event"] = "SUCCESS";

type TransitionProps = {
  event: "SUCCESS" | "ERROR" | "START";
};

export const ORIGIN_HELPER_TEXT = "Clique ou arraste para o fluxograma";

export const BLOCKS_TITLE = "BLOCOS";

export const PIPELINE_RUNNING_LABEL = "Em andamento";

export const PIPELINE_FINISHED_LABEL = "Finalizado";

export const NODE_STATS_SUCCESS_CODE = 400;

export const START_NODE_LABEL = "Origem CSV";

export const END_NODE_LABEL = "Fim";

export const PANEL_EMPTY_STATE_TITLE = "Selecione um nó";

export const PANEL_EMPTY_STATE_SUBTITLE =
  "Clique em um bloco do fluxograma para editar seus campos.";

export const BLOCKS_LOCKED_SUBTITLE =
  "Pipeline em andamento: blocos desabilitados";
